<template>
    <div
        v-if="item.descriptionHtml"
        class="item-description-wrapper"
        data-test="timeline-item-description"
        :class="{ 'text-truncated': isDescriptionTooLong, 'show-full-description': shouldShowFullDescription }"
    >
        <p
            class="item-description"
            :class="{ 'padding-bottom': isDescriptionTooLong }"
            v-html="item.descriptionHtml"
        />
        <a
            v-if="isDescriptionTooLong"
            href="#"
            class="text-continue-reading"
            data-test="timeline-item-description-continue-reading"
            @click.stop.prevent="toggleShowFullDescription"
        >
            <span v-if="!shouldShowFullDescription">{{ $t('timeline.item.view_full_post') }}</span>
            <span v-else>{{ $t('timeline.item.hide_full_post') }}</span>
        </a>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue';
    import type { PropType } from 'vue';
    import type { ITimelineItem } from '~/types/types';
    import { stripTags } from '~/utils/helpers';
    import { MAX_DESCRIPTION_LENGTH } from '~/utils/constants';

    const shouldShowFullDescription = ref<boolean>(false);

    const props = defineProps({
        item: {
            type: Object as PropType<ITimelineItem>,
            required: true,
        }
    });

    const isDescriptionTooLong = computed(() => {
        if (!props.item.descriptionHtml) {
            return false;
        }

        return stripTags(props.item.descriptionHtml).length >= MAX_DESCRIPTION_LENGTH;
    });

    const toggleShowFullDescription = () => {
        shouldShowFullDescription.value = !shouldShowFullDescription.value;
    };
</script>

<style scoped lang="scss">
    .item-description {
        transition: all 0.3s ease;
    }

    .padding-bottom {
        padding-bottom: 30px;
    }

    .text-truncated {
        overflow: hidden;
        max-height: 2rem;
        padding-bottom: 4.4rem;
        position: relative;
        transition: all 0.3s ease;

        .text-continue-reading {
            position: absolute;
            bottom: 0;
            left: 0;
            font-size: 14px;
            z-index: 2;
            transition: all 0.3s ease;
        }

        &:after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 1;
            height: 4rem;
            pointer-events: none;
            background: linear-gradient(to top, #fff 30%, rgba(255, 255, 255, 0) 100%);
            transition: all 0.3s ease;
        }

        &.show-full-description {
            max-height: none;
            padding-bottom: 0;
            transition: all 0.3s ease;

            &:after {
                transition: all 0.3s ease;
                display: none;
            }
        }
    }
</style>
